import Overlay from 'utils/overlay';
import { transitionEnd, repaint } from 'utils/node';

type Events = 'open' | 'opened' | 'close' | 'closed';

export default class {
  public isActive: boolean;

  private readonly container: HTMLElement;

  private focusTrap;

  protected classes: { active: string };

  private overlay: Overlay;

  private overlayElement: HTMLElement;

  private overlayClass: string;

  private closeTrigger: string;

  constructor(
    element: string | HTMLElement,
    overlayElement: HTMLElement = document.body,
    overlayClass: string = '',
  ) {
    this.container = typeof element === 'string'
      ? document.querySelector(element)
      : element;

    if (!(this.container instanceof HTMLElement)) {
      throw new TypeError(typeof element === 'string'
        ? `Could not find element matching selector: ${element}`
        : `HTMLElement was not passed to constructor. Instead ${element} was passed`);
    }

    this.isActive = false;
    this.container.hidden = true;
    this.closeTrigger = 'data-close';

    this.overlayElement = overlayElement;
    this.overlay = new Overlay(this.overlayElement, overlayClass);

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);

    this.on('open', async () => {
      if (!this.focusTrap) {
        const { default: focusTrap } = await import(/* webpackChunkName: "focus-trap" */ 'modules/focus-trap');

        this.focusTrap = focusTrap.createFocusTrap(this.container, {
          allowOutsideClick: true,
          fallbackFocus: '[tabindex="0"]',
        });
      }

      this.focusTrap.activate();
    });

    this.on('close', () => {
      this.focusTrap?.deactivate();
    });
  }

  private addEventListeners(): void {
    document.addEventListener('touchstart', this.clickEvent);
    document.addEventListener('click', this.clickEvent);
    document.addEventListener('keydown', this.keydownEvent);
  }

  private removeEventListeners(): void {
    document.removeEventListener('touchstart', this.clickEvent);
    document.removeEventListener('click', this.clickEvent);
    document.removeEventListener('keydown', this.keydownEvent);
  }

  private clickEvent(event: MouseEvent | TouchEvent): void {
    const target = event.target as HTMLElement;

    if (!(event.target instanceof Node)) {
      return;
    }

    if (target === this.overlay.element) {
      event.stopPropagation();
      this.close();
    }

    if (target.hasAttribute(this.closeTrigger)) {
      const value = target.getAttribute(this.closeTrigger);

      if (value !== '') {
        const relative = target.closest('[aria-modal="true"]');

        if (this.container.id === relative.id) {
          event.stopPropagation();
          this.close();
        }
      } else if (this.container.contains(target)) {
        event.stopPropagation();
        this.close();
      }
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  toggle(): void {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isActive) {
      return;
    }

    this.dispatchEvent('open');

    this.isActive = true;
    this.container.hidden = false;
    repaint(this.container);

    this.container.classList.add(this.classes.active);
    this.overlay.show();

    setTimeout(() => {
      this.addEventListeners();
    }, 0);

    this.dispatchEvent('opened');
  }

  close(): void {
    if (this.isActive === false) {
      return;
    }

    this.dispatchEvent('close');

    this.isActive = false;
    this.container.classList.remove(this.classes.active);
    this.overlay.hide();

    this.removeEventListeners();

    transitionEnd(this.container, () => {
      if (this.isActive === false) {
        this.container.hidden = true;
      }
    }, { once: true });

    this.dispatchEvent('closed');
  }

  private dispatchEvent(type: Events): void {
    const event = new Event(type);

    this.container.dispatchEvent(event);
  }

  on(type: Events, callback: CallableFunction): void {
    this.container.addEventListener(type, (event) => callback(event));
  }
}
